import { Injectable } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { AuthService } from './auth.service';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AccessManagementService } from './access-management.service';
import { JwtHelperService } from 'app/shared/jwt-helper/jwt-helper.service';
import { AngularFireAuth } from '@angular/fire/auth';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {

  constructor(private authService: AuthService,
    private router: Router,
    private accessManagementService: AccessManagementService,
    public afAuth: AngularFireAuth,
    private jwtHelper: JwtHelperService) {}

  canActivate(): Observable<boolean> {

    const fsLogin = this.afAuth?.user?.pipe(take(1), map(user => {
      const valid = !!user
      if(!valid || !user || !user?.uid ){
        this.router.navigate(['/login']);
      }
      return valid;
    }));

    if(!fsLogin){
      this.router.navigate(['/login']);
      return of(false);
    }

    const auth = localStorage.getItem('acmToken');
    if(!auth || auth === 'undefined'){
      this.router.navigate(['/login']);
      return of(false);
    }
    const tokenExpired = this.hasTokenExpired(auth);
    if(tokenExpired) {
      const refreshToken = localStorage.getItem('refreshToken');
      if(!refreshToken || refreshToken === 'undefined'){
        this.router.navigate(['/login']);
        return of(false);
      }
      return this.refresh(refreshToken, fsLogin);
    }
    return fsLogin;
  }

  // A token we can't read is treated as expired, so it goes through refresh instead of being let through.
  private hasTokenExpired(authToken): boolean {
    try{
      const decoded = this.jwtHelper.parseJwt(authToken);
      if(decoded?.exp){
        const date = decoded.exp;
        const currentDay = Math.floor(new Date().getTime()/1000.0)
        return (date - currentDay) <= 0;
      }
      return true;
    } catch {
      return true;
    }
  }

  private refresh(refreshToken: string, fsLogin: Observable<boolean>): Observable<boolean> {
    return this.authService.refreshToken(refreshToken).pipe(
      take(1),
      switchMap((value) => {
        // ErrorInterceptor can hand an error back as a value; without a new token there's no session.
        if (!value?.AccessToken) {
          this.router.navigate(['/login']);
          return of(false);
        }
        this.accessManagementService.setACMToken(value.AccessToken);
        this.accessManagementService.setRefreshToken(value.RefreshToken);
        return fsLogin;
      }),
      catchError(() => {
        this.router.navigate(['/login']);
        return of(false)
      })
    );
  }

}
